import React, {useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {GroupSuggest} from 'src/components/group-suggest';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {RequiredMark} from 'src/components/required-mark';
import {
    NO_RESULTS_MESSAGE,
    STACKOVERFLOW_MESSAGE,
} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useLicenseCommonLimits, useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {downloadUserListReport, getUserListIds} from 'src/openapi/admin/apis/UserListReportApi';
import {UserStateCriteria as UserStates} from 'src/openapi/admin/models/UserStateCriteria';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Typography from '@pb/kit-component-typography';

import {UserStateSuggest} from '../../components/user-state-suggest';
import {REPORTS_SUB_TABS} from '../../constants';
import {ReportsTabs} from '../../report-tabs';

import {
    CHUNK_SIZE,
    FILE_NAME,
    GET_USERS_TASK_NAME,
    GET_USERS_TASK_PROGRESS_LABEL,
    REPORT_NAME,
    STATIC_FILE_HEADERS,
    TASK_NAME,
} from './constants';
import {getUsageLimitsHeaders, getUsageLimitsValues} from './utils';

import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';
import type {UserStateCriteria} from 'src/openapi/admin/models/UserStateCriteria';
import type {CheckBoxChangeData} from 'src/typedef';

export const UserListReport = () => {
    const queryParams = useParams<{accountId: string, licenseId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const licenseId = queryParams.licenseId ? +queryParams.licenseId : -1;

    const {accountReportRun} = useEventTracking();

    const apiDispatch = useAppDispatch();
    const createBackgroundTask = useBackgroundTaskManager();
    const {licenseCommonLimits} = useLicenseCommonLimits();
    const {getUserStatusByCode} = useUserStatuses();
    const {getUserTypeByCode} = useUserTypes();
    const [includeUsageLimits, setIncludeUsageLimits] = useState<boolean>(false);
    const [includeUsedLimits, setIncludeUsedLimits] = useState<boolean>(false);
    const [includeLoginLinks, setIncludeLoginLinks] = useState<boolean>(false);
    const [includeUserNotes, setIncludeUserNotes] = useState<boolean>(false);
    const [selectedUserStates, selectUserStates] = useState<UserStateCriteria[]>([UserStates.LICENSED]);
    const [selectedGroupId, selectGroupId] = useState<number | null>(null);


    const changeIncludeUsageLimits = useCallback(({event}: CheckBoxChangeData) => {
        setIncludeUsageLimits(event.currentTarget.checked);
    }, []);

    const changeIncludeUsedLimits = useCallback(({event}: CheckBoxChangeData) => {
        setIncludeUsedLimits(event.currentTarget.checked);
    }, []);

    const changeIncludeLoginLinks = useCallback(({event}: CheckBoxChangeData) => {
        setIncludeLoginLinks(event.currentTarget.checked);
    }, []);

    const changeIncludeUserNotes = useCallback(({event}: CheckBoxChangeData) => {
        setIncludeUserNotes(event.currentTarget.checked);
    }, []);

    const changeSelectedGroup = useCallback((group: AccountGroup | null) => {
        selectGroupId(group?.id || null);
    }, []);

    const runReport = useCallback(async (handler: (ids: number[]) => void) => {
        const getUsersTask = createBackgroundTask(
            GET_USERS_TASK_NAME,
            <b>{GET_USERS_TASK_PROGRESS_LABEL}</b>,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (!getUsersTask) {
            return;
        }

        const userIdsResponse = await apiDispatch(getUserListIds({
            accountId,
            states: selectedUserStates,
            groupId: selectedGroupId || undefined,
        }));

        if (userIdsResponse.payload.success && userIdsResponse.payload.data.length) {
            getUsersTask.completeSuccess();
            handler(userIdsResponse.payload.data);
        } else if (userIdsResponse.payload.success) {
            getUsersTask.completeFailure(<b>{NO_RESULTS_MESSAGE}</b>);
            accountReportRun({reportType: REPORT_NAME, success: false});
        } else {
            getUsersTask.completeFailure(<b>{getError({error: userIdsResponse.payload.data, separator: ''})}</b>);
            accountReportRun({reportType: REPORT_NAME, success: false});
        }
    }, [selectedUserStates, selectedGroupId, accountId, apiDispatch, createBackgroundTask, accountReportRun]);

    const getFullName = (firstName?: string, lastName?: string) => {
        return [firstName, lastName].filter(Boolean).join(' ');
    };


    return (
        <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
            <ReportsTabs
                accountId={accountId}
                licenseId={licenseId}
                activeSubTab={REPORTS_SUB_TABS.USER_LIST.name}
            />
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {REPORTS_SUB_TABS.USER_LIST.caption}
                        </Typography>
                        <ChunkedDownloadTool
                            taskName={TASK_NAME}
                            fileName={FILE_NAME}
                            fileHeaders={{
                                ...STATIC_FILE_HEADERS,
                                ...((includeUsageLimits || includeUsedLimits)
                                    ? getUsageLimitsHeaders(
                                            licenseCommonLimits,
                                            includeUsageLimits,
                                            includeUsedLimits,
                                        ) : {}
                                ),
                                ...(includeLoginLinks ? {loginLink: 'Login Link'} : {}),
                                ...(includeUserNotes ? {userNotes: 'User Notes'} : {}),
                            }}
                            getData={async ids => {
                                const chunkDataResponse = await apiDispatch(downloadUserListReport({
                                    userListRequest: {
                                        ids,
                                        includeUsageLimits,
                                        includeUsedLimits,
                                        includeLoginLinks,
                                        includeUserNotes,
                                    },
                                }));
                                if (chunkDataResponse.payload.success) {
                                    const responseData = chunkDataResponse.payload.data;
                                    const data = responseData.map(item => {
                                        const usageLimits = (item.usageLimits || item.usedLimits)
                                            ? getUsageLimitsValues({
                                                    dictionary: licenseCommonLimits,
                                                    includeUsed: includeUsedLimits,
                                                    includeTotal: includeUsageLimits,
                                                    usageLimits: item.usageLimits,
                                                    usedLimits: item.usedLimits,
                                                }) : {};
                                        return {
                                            ...item,
                                            ...usageLimits,
                                            userType: getUserTypeByCode(item.userType)?.description,
                                            userStatus: getUserStatusByCode(item.userStatus)?.description,
                                            newsletters: item.newsletters?.join('; ') || 'No Newsletter',
                                            createdDate: item.audit?.created.eventTime,
                                            creatorId: item.audit?.created.modifierInfo.userId,
                                            creatorFullName: getFullName(
                                                item.audit?.created.modifierInfo.userFirstName,
                                                item.audit?.created.modifierInfo.userLastName,
                                            ),
                                            lastUpdatedDate: item.audit?.lastUpdated.eventTime,
                                            lastModifierId: item.audit?.lastUpdated.modifierInfo.userId,
                                            lastModifierFullName: getFullName(
                                                item.audit?.lastUpdated.modifierInfo.userFirstName,
                                                item.audit?.lastUpdated.modifierInfo.userLastName,
                                            ),
                                            lastUpdateSource: item.audit?.lastUpdated.modifierSource,
                                            lastUpdateReason: item.audit?.lastUpdated.modifierReason,
                                        };
                                    });

                                    return Promise.resolve({type: '', payload: {
                                        success: true,
                                        error: false,
                                        data,
                                    }});
                                } else {
                                    return Promise.resolve({type: '', payload: chunkDataResponse.payload});
                                }
                            }}
                            onSuccess={() => accountReportRun({reportType: REPORT_NAME, success: true})}
                            onError={() => accountReportRun({reportType: REPORT_NAME, success: false})}
                            chunkSize={CHUNK_SIZE}>
                            {handler => (
                                <Button
                                    onClick={() => runReport(handler)}
                                    disabled={!selectedUserStates.length}
                                    variant="Type.PRIMARY">
                                    Run Report
                                </Button>
                            )}
                        </ChunkedDownloadTool>
                    </Group>
                </Item>
            )}>
                <FormSection headline="Configuration">
                    <Group direction="v" alignItems="start">
                        <div style={{width: 300}}>
                            <Label><RequiredMark label="Include"/></Label>
                            <CheckBox
                                label="Include used limits"
                                checked={includeUsedLimits}
                                onCheckBoxChange={changeIncludeUsedLimits}
                            />
                            <CheckBox
                                label="Include overall limits"
                                checked={includeUsageLimits}
                                onCheckBoxChange={changeIncludeUsageLimits}
                            />
                            <CheckBox
                                label="Include login links"
                                checked={includeLoginLinks}
                                onCheckBoxChange={changeIncludeLoginLinks}
                            />
                            <CheckBox
                                label="Include user notes"
                                checked={includeUserNotes}
                                onCheckBoxChange={changeIncludeUserNotes}
                            />
                        </div>
                        <div style={{width: 300}}>
                            <Label><RequiredMark label="User States"/></Label>
                            <UserStateSuggest
                                userStates={selectedUserStates}
                                onSelect={selectUserStates}
                            />
                        </div>
                        <div style={{width: 300}}>
                            <Label htmlFor="user-list-group-suggest">Select Group</Label>
                            <GroupSuggest
                                id="user-list-group-suggest"
                                width={300}
                                accountId={accountId}
                                placeholder="All Groups"
                                selectedGroupId={selectedGroupId}
                                onSelect={changeSelectedGroup}
                            />
                        </div>
                    </Group>
                </FormSection>
            </ProfileMain>
        </ErrorBoundary>
    );
};
